<template>
  <DraftMain
    :panelTitle="panelTitle"
    :initialValues="initialValues"
    :confirmDeletionFn="confirmDeletionFn"
    :closeModal="closeModal"
    :onSearch="onSearch"
    :showModal="showModal"
  >
    <template #searchHeadOperation>
      <div class="flex items-center justify-center gap-2"></div>
    </template>
    <template #searchPanel>
      <div class="flex flex-row flex-wrap w-full">
        <Field name="unifiedNumber" v-slot="{ field, errors: errs }">
          <SimpleTextInput
            :field="field"
            :errors="errs"
            :label="$t('unifiedNumber')"
            :maxLength="80"
            direction="flex-col"
            :isRequired="false"
            class="lg:w-1/2 flex-col px-2 w-full"
          />
        </Field>
        <Field name="identityNumber" v-slot="{ field, errors: errs }">
          <SimpleTextInput
            :field="field"
            :errors="errs"
            :label="$t('identityNumber')"
            :maxLength="80"
            direction="flex-col"
            :isRequired="false"
            class="lg:w-1/2 flex-col px-2 w-full"
          />
        </Field>
      </div>
    </template>
    <template #searchResult>
      <DraftTable
        :isLoading="isLoading"
        :tableData="tableData"
        :initialValues="initialValues"
        :pageLength="pageLength"
        @onPageClick="onPageClick"
        @selected="selectedNumberOfRow"
        @checked="selectedRow"
        @checkOut="checkOut"
        @checkIn="checkIn"
        @delete="deleteSelected"
        :tableHeaders="tableHeaders"
        :is-show-lock-feature="false"
      >
        <template #tableRow="{ request }">
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request?.personUnifiedNumber }}
          </td>
          <td
            class="min-w-[180px] px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            <span v-html="checkOwnerName(request)"></span>
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-xs font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request?.serviceTransactionName }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-xs font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request?.lookupLastActionId }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ formattedCreationDate(request?.creationDate) }}
          </td>
        </template>

        <template #tableOperation="{ request }">
          <button
            class="bg-primary size-6 rounded-md text-white cursor-pointer text-center"
            @click.prevent="viewTicket(request)"
          >
            <i class="fa fa-eye"></i>
          </button>
        </template>
      </DraftTable>
    </template>
  </DraftMain>
</template>
<script setup>
import { onBeforeMount,  ref } from 'vue'
import { notify } from 'notiwind'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { DateTime } from 'luxon'
import { Field } from 'vee-validate'
import DraftMain from '@/views/individual/draft/Main.vue'

import SimpleTextInput from '@/components/SimpleForm/TextInput.vue'
import DraftTable from '@/views/individual/draft/DraftTable.vue'
import apiHelperService from '@/services'

const route = useRoute()
const router = useRouter()
const i18n = useI18n()

const draftId = ref([])
const showModal = ref(false)
const panelTitle = ref('EidExceptionRequests')
const isLoading = ref(false)
const initialValues = ref({
  currentPage: 1,
  pageSize: 10
})

const pageLength = ref(10)
const tableData = ref([])
const currentSearchValues = ref()

const tableHeaders = ref([
  {
    value: 1,
    label: ''
  },
  {
    value: 2,
    label: i18n.t('RequestNumber')
  },
  {
    value: 3,
    label: i18n.t('serviceBeneficiaryname')
  },
  {
    value: 4,
    label: i18n.t('RequestType')
  },
  {
    value: 5,
    label: i18n.t('requestStatus')
  },
  {
    value: 6,
    label: i18n.t('CreationDate')
  }
])

const viewTicket = (request) => {
  router.push(`/eid-exception-requests-view/${request.id}`)
}

const closeModal = () => {
  showModal.value = false
}

const formattedCreationDate = (date) => {
  if (!date) return
  return DateTime.fromISO(date).toFormat('dd/MM/yyyy hh:mm a')
}

const checkOwnerName = (request) => {
  return request.englishFullName + '<br>' + request.arabicFullName
}

const requestTableData = async (value) => {
  isLoading.value = true
  const payload = {
    ...value
  }
  const params = {
    currentPage: initialValues.value.currentPage,
    pageSize: pageLength.value
  }
  currentSearchValues.value = value

  router.replace({ query: value })
  try {
    const response = await apiHelperService.post(
      '/api/icaRequest/exceptionRequests/search',
      payload,
      {
        params
      }
    )
    if (response) {
      isLoading.value = false
      return response
    }
  } catch (error) {
    console.error('requestTableData', error)
  }
}

const selectedNumberOfRow = async (num) => {
  let searchParams
  pageLength.value = num

  if (currentSearchValues.value) {
    searchParams = {
      ...currentSearchValues.value,
      pageSize: +pageLength.value,
      currentPage: 1
    }
  } else {
    searchParams = {
      ...initialValues.value,
      pageSize: +pageLength.value,
      currentPage: 1
    }
  }

  tableData.value = await requestTableData(searchParams)
}

const onPageClick = async (pageNum) => {
  let searchParams
  if (currentSearchValues.value) {
    searchParams = {
      ...currentSearchValues.value,
      currentPage: +pageNum
    }
  } else {
    searchParams = {
      ...initialValues.value,
      currentPage: +pageNum
    }
  }

  tableData.value = await requestTableData(searchParams)
}

const onSearch = async (value) => {
  tableData.value = await requestTableData(value)
}

const selectedRow = (draft) => {
  if (!draft) return
  draftId.value = draft
}

const deleteSelected = (draft) => {
  draftId.value = draft
  showModal.value = true
}

const checkOut = async () => {
  let searchParams = currentSearchValues.value || initialValues.value
  tableData.value = await requestTableData(searchParams)
  notify(
    {
      group: 'success',
      title: i18n.t('Success'),
      text: i18n.t('checkedOutSuccessfully')
    },
    6000
  )
}

const checkIn = async () => {
  let searchParams = currentSearchValues.value || initialValues.value
  tableData.value = await requestTableData(searchParams)
  notify(
    {
      group: 'success',
      title: i18n.t('Success'),
      text: i18n.t('checkedInSuccessfully')
    },
    6000
  )
}

const confirmDeletionFn = async () => {
  try {
    const payload = draftId.value
    const response = await apiHelperService.post('/api/draft/delete', payload)
    if (response) {
      let searchParams = currentSearchValues.value || initialValues.value
      tableData.value = await requestTableData(searchParams)
      showModal.value = false
      notify(
        {
          group: 'success',
          title: i18n.t('Success'),
          text: i18n.t('DeletedSuccessfully')
        },
        6000
      )
    }
  } catch (error) {
    console.error('confirmDeletionFn', error)
  }
}

const setDraftResponse = async () => {
  tableData.value = await requestTableData(initialValues.value)
}

onBeforeMount(async () => {
  await setDraftResponse()
  const searchParams = route.query
  initialValues.value = searchParams
})
</script>
<style></style>
